import {injectable, Installer, inject, CoreDI} from '@gongxigames/framework/core';
import {GameClientWrapper, SlotSettings} from '@gongxigames/framework/slot';

interface GameClientWrapperStrategy {
    priority: number;
    name: string;
    check: () => boolean;
}

@injectable()
export class GameClientWrapperFactory {
    public static readonly NAME: string = 'GameClientWrapperFactory';

    @inject(CoreDI.Installer)
    private systemComponents!: Installer;

    @inject(CoreDI.Settings)
    private settings!: SlotSettings;

    public create<G extends GameClientWrapper>(): G {
        return this.systemComponents.getComponent<G>(this.getName());
    }

    private getName(): string {
        const anyIntegration = this.settings.get('integrationType') as string;

        const strategies: GameClientWrapperStrategy[] = [
            { priority: 1, name: 'pop', check: this.shouldForcePop.bind(this) },
            { priority: 2, name: anyIntegration, check: this.hasIntegrationType.bind(this) },
            { priority: 3, name: 'pariplay', check: this.shouldForcePariplay.bind(this) },
            { priority: 4, name: 'offline', check: this.isOffline.bind(this) },
        ];

        strategies.sort((a, b) => a.priority - b.priority);

        for (const strategy of strategies) {
            if (strategy.check()) {
                return strategy.name;
            }
        }

        const studios: GameClientWrapperStrategy[] = [
            { priority: 1, name: 'koala', check: this.isKoala.bind(this) },
        ];

        studios.sort((a, b) => a.priority - b.priority);

        for (const studio of studios) {
            if (studio.check()) {
                return studio.name;
            }
        }

        return 'rubyplay';
    }

    private isKoala(): boolean {
        return this.settings.get('gameName').startsWith('kg_') && !this.settings.get('offlineMode');
    }

    private isOffline(): boolean {
        return this.settings.get('offlineMode');
    }

    private shouldForcePop(): boolean {
        return this.settings.get('integrationType') === 'ucip' && this.isNotInIframe();
    }

    private hasIntegrationType(): boolean {
        const integrationType = this.settings.get('integrationType');

        return typeof integrationType === 'string' && integrationType.length > 0;
    }

    private shouldForcePariplay(): boolean {
        return window.location.href.includes('/pariplay/launcher') &&
               window.location.href.includes('gameCode=');
    }

    private isNotInIframe(): boolean {
        return window.self === window.top;
    }
}
